import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSession, useUser, useClerk } from '@clerk/react';
import axios from '../utils/axiosInstance';
import { setClerkTokenGetter } from '../utils/axiosInstance';

// Create authentication context
const AuthContext = createContext();

// Custom hook to use authentication context
export const useAuth = () => useContext(AuthContext);

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { session } = useSession();
  const { isSignedIn, isLoaded: clerkLoaded } = useUser();
  const { signOut } = useClerk();
  const loggingOut = useRef(false);
  const channelRef = useRef(null);

  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await axios.get('/users/me/');
      localStorage.setItem('userDetails', JSON.stringify(response.data));
      setUser(response.data);
    } catch (e) {
      console.error('Error fetching user details:', e);
    }
  }, []);

  const logout = useCallback(async (sendMessage = true) => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    setLoading(true);
    localStorage.removeItem('userDetails');
    setUser(null);

    if (sendMessage) {
      channelRef.current?.postMessage({ type: 'logout' });
    }

    try {
      await signOut();
    } catch (e) {
      console.error('Error signing out of Clerk:', e);
    }
  }, [signOut]);

  // Token getter: only depends on session
  useEffect(() => {
    if (session) {
      setClerkTokenGetter(() => session.getToken());
    } else {
      setClerkTokenGetter(null);
    }
    return () => setClerkTokenGetter(null);
  }, [session]);

  // Fetch user details once Clerk session is ready
  useEffect(() => {
    if (!clerkLoaded) return;

    if (isSignedIn && session && !user && !loggingOut.current) {
      fetchUserDetails().finally(() => setLoading(false));
    } else if (!isSignedIn) {
      loggingOut.current = false;
      setUser(null);
      setLoading(false);
    }
  }, [clerkLoaded, isSignedIn, session, user, fetchUserDetails]);

  // Cross-tab sync
  useEffect(() => {
    const channel = new BroadcastChannel('auth');
    channelRef.current = channel;
    channel.onmessage = (event) => {
      if (event.data.type === 'logout') {
        logout(false);
      } else if (event.data.type === 'login') {
        fetchUserDetails();
      }
    };
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [logout, fetchUserDetails]);

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
